import {
  EditOutlined,
  FilterOutlined,
  FormOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { TableProps } from 'antd';
import { Button, Input, Radio, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import { useCallback, useMemo, useState } from 'react';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useGetEmailTemplatesQuery } from '@/modules/email-templates/emailTemplatesApi';
import type { EmailTemplate } from '@/types/emailTemplate';
import { EMAIL_SIGNATURE_KEY, storageToFriendlyTemplate } from '@/types/emailTemplate';
import { ColumnFilterPanel } from '../categories/components/ColumnFilterPanel';
import { SortableColumnTitle } from '../categories/components/SortableColumnTitle';
import { EmailTemplateFormModal } from './components/EmailTemplateFormModal';
import styles from './EmailTemplates.module.scss';

type StatusFilter = 'all' | 'active' | 'inactive';
type SortField = 'name' | 'subject' | 'updatedAt';
type SortOrder = 'asc' | 'desc';

function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

export default function EmailTemplates() {
  const { data: listResult, isLoading, isFetching } = useGetEmailTemplatesQuery();
  useGlobalLoader(isFetching && !!listResult);

  const templates = listResult?.results ?? [];
  const signatureTemplate = templates.find((item) => item.key === EMAIL_SIGNATURE_KEY) ?? null;
  const messageTemplates = templates.filter((item) => item.key !== EMAIL_SIGNATURE_KEY);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [statusDraft, setStatusDraft] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortField>('updatedAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);

  const hasActiveFiltersOrSort =
    search.length > 0 || status !== 'all' || sortBy !== 'updatedAt' || sortOrder !== 'desc';

  const filteredSorted = useMemo(() => {
    const query = search.trim().toLowerCase();
    let rows = messageTemplates.filter((item) => {
      if (status === 'active' && !item.isActive) return false;
      if (status === 'inactive' && item.isActive) return false;
      if (!query) return true;
      return (
        item.name.toLowerCase().includes(query) ||
        item.subject.toLowerCase().includes(query)
      );
    });

    rows = [...rows].sort((a, b) => {
      let result = 0;
      if (sortBy === 'name') result = compareText(a.name, b.name);
      else if (sortBy === 'subject') result = compareText(a.subject, b.subject);
      else {
        const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        result = aTime - bTime;
      }
      return sortOrder === 'asc' ? result : -result;
    });

    return rows;
  }, [messageTemplates, search, status, sortBy, sortOrder]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const handleSort = useCallback(
    (field: string) => {
      const nextField = field as SortField;
      if (sortBy === nextField) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(nextField);
        setSortOrder('asc');
      }
      setPage(1);
    },
    [sortBy],
  );

  const openCreate = () => {
    setEditingTemplate(null);
    setModalOpen(true);
  };

  const openEdit = useCallback((template: EmailTemplate) => {
    setEditingTemplate(template);
    setModalOpen(true);
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setEditingTemplate(null);
  };

  const handleReset = () => {
    setSearch('');
    setSearchDraft('');
    setStatus('all');
    setStatusDraft('all');
    setSortBy('updatedAt');
    setSortOrder('desc');
    setPage(1);
  };

  const handleTableChange: TableProps<EmailTemplate>['onChange'] = (pagination) => {
    setPage(pagination.current ?? 1);
    setPageSize(pagination.pageSize ?? 10);
  };

  const columns: ColumnsType<EmailTemplate> = useMemo(
    () => [
      {
        title: (
          <SortableColumnTitle
            label="NAME"
            field="name"
            activeField={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'name',
        key: 'name',
        className: styles.nameCol,
        filterIcon: (filtered) => (
          <SearchOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: search ? [search] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              setSearch(searchDraft.trim());
              setPage(1);
              close();
            }}
            onReset={() => {
              setSearchDraft('');
              setSearch('');
              setPage(1);
              close();
            }}
          >
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined />}
              placeholder="Search name or subject"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              onPressEnter={() => {
                setSearch(searchDraft.trim());
                setPage(1);
                close();
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setSearchDraft(search);
          },
        },
        render: (name: string) => <span className={styles.nameCell}>{name}</span>,
      },
      {
        title: (
          <SortableColumnTitle
            label="SUBJECT"
            field="subject"
            activeField={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'subject',
        key: 'subject',
        className: styles.subjectCol,
        render: (_subject: string, record) => (
          <span className={styles.subjectCell}>
            {storageToFriendlyTemplate(record.subject, record.availableVariables ?? [])}
          </span>
        ),
      },
      {
        title: 'STATUS',
        dataIndex: 'isActive',
        key: 'isActive',
        width: 170,
        align: 'center',
        className: styles.statusCol,
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: status !== 'all' ? [status] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              setStatus(statusDraft);
              setPage(1);
              close();
            }}
            onReset={() => {
              setStatusDraft('all');
              setStatus('all');
              setPage(1);
              close();
            }}
          >
            <Radio.Group
              value={statusDraft}
              onChange={(event) => setStatusDraft(event.target.value)}
              className={styles.statusRadio}
            >
              <Radio value="all">All</Radio>
              <Radio value="active">Active</Radio>
              <Radio value="inactive">Inactive</Radio>
            </Radio.Group>
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setStatusDraft(status);
          },
        },
        render: (isActive: boolean, record) => (
          <div className={styles.statusTags}>
            <Tag color={isActive ? 'success' : 'default'}>{isActive ? 'Active' : 'Inactive'}</Tag>
            {record.includeSignature ? <Tag color="blue">Signature</Tag> : null}
          </div>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="UPDATED DATE"
            field="updatedAt"
            activeField={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 140,
        render: (value?: string) => {
          if (!value) return <span className={styles.dateCell}>—</span>;
          const date = new Date(value);
          if (Number.isNaN(date.getTime())) return <span className={styles.dateCell}>—</span>;
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return <span className={styles.dateCell}>{`${day}-${month}-${year}`}</span>;
        },
      },
      {
        title: 'ACTION',
        key: 'action',
        width: 90,
        align: 'center',
        className: styles.actionCol,
        render: (_value, record) => (
          <Button
            type="primary"
            size="small"
            className={styles.editBtn}
            icon={<EditOutlined />}
            aria-label={`Edit ${record.name}`}
            onClick={() => openEdit(record)}
          />
        ),
      },
    ],
    [
      sortBy,
      sortOrder,
      handleSort,
      search,
      searchDraft,
      status,
      statusDraft,
      openEdit,
    ],
  );

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            {hasActiveFiltersOrSort ? (
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Reset
              </Button>
            ) : null}
            <Button
              icon={<FormOutlined />}
              onClick={() => signatureTemplate && openEdit(signatureTemplate)}
              disabled={!signatureTemplate}
            >
              Update default signature
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Create template
            </Button>
          </>
        }
      />

      <div className={styles.tableCard}>
        <Table<EmailTemplate>
          className={styles.table}
          rowKey="id"
          size="small"
          loading={isLoading && !listResult}
          columns={columns}
          dataSource={pagedRows}
          onChange={handleTableChange}
          pagination={{
            current: page,
            pageSize,
            total: filteredSorted.length,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (total, [start, end]) => `${start}-${end} of ${total} records`,
            size: 'small',
          }}
        />
      </div>

      <EmailTemplateFormModal
        open={modalOpen}
        template={editingTemplate}
        onClose={closeModal}
      />
    </div>
  );
}
